/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NetworkSecurityPolicyModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Type,
} from '@angular/core';

import { ClrFormLayout } from '@clr/angular';
import { L10nService } from '@vmw/ngx-vip';

import {
    NetworkSecurityMatchTarget,
    NetworkSecurityPolicyActionRLParam,
} from 'object-types';

import {
    networkSecurityPolicyActionTypeHash,
    NetworkSecurityRuleConfigItem,
} from 'ajs/modules/network-security-policy/factories/network-security-rule.config-item.factory';

import {
    GeoMatchComponent,
    IMatchOption,
    IpAddrMatchComponent,
    IpReputationTypeMatchComponent,
    PortMatchComponent,
} from 'ng/modules/match/components';

import * as globalL10n from 'global-l10n';
import * as l10n from './network-security-rule-modal.l10n';
import './network-security-rule-modal.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Modal component for Network Security Rule ObjectTypeItem.
 *
 * @author Abhinesh Gour, Umesh Chovatiya
 */
@Component({
    selector: 'network-security-rule-modal',
    templateUrl: './network-security-rule-modal.component.html',
})
export class NetworkSecurityRuleModalComponent implements OnInit {
    /**
     * NetworkSecurityRule ObjectTypeItem instance.
     */
    @Input()
    public editable: NetworkSecurityRuleConfigItem;

    /**
     * Informs if current item is new or already exists.
     */
    @Input()
    public isEditing: boolean;

    /**
     * Called when the user wants to save the modal.
     */
    @Output()
    public onSubmit = new EventEmitter();

    /**
     * Called when the user wants to cancel editing the modal.
     */
    @Output()
    public onCancel = new EventEmitter();

    /**
     * NetworkSecurityRule modal clr-form layout.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * For template usage.
     */
    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * NetworkSecurityRule objectType.
     */
    public objectType: string;

    /**
     * Options list for all possible options
     */
    public matchOptions: IMatchOption[];

    /**
     * List of all required object types.
     */
    public readonly objectRulesType = {
        networkSecurityPolicyActionRLParam: NetworkSecurityPolicyActionRLParam,
    };

    /**
     * Holds values of networkSecurityPolicyActionType enum to use in template.
     */
    public readonly networkSecurityPolicyActionType = networkSecurityPolicyActionTypeHash;

    constructor(private readonly l10nService: L10nService) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.objectType = this.editable.messageType;
        this.setMatchOptions();
    }

    /**
     * Triggered upon user attempting to save settings.
     */
    public handleSubmit(): void {
        this.onSubmit.emit();
    }

    /**
     * Triggered upon user exiting modal.
     */
    public handleCancel(): void {
        this.onCancel.emit();
    }

    /**
     * Sets options for Network Security Policy Rule Matches.
     */
    private setMatchOptions(): void {
        this.matchOptions = [
            {
                component: IpAddrMatchComponent as Type<Component>,
                label: this.l10nService.getMessage(l10nKeys.clientIpLabel),
                objectType: NetworkSecurityMatchTarget,
                fieldName: 'client_ip',
            },
            {
                component: IpReputationTypeMatchComponent as Type<Component>,
                label: this.l10nService.getMessage(l10nKeys.ipReputationMatchLabel),
                objectType: NetworkSecurityMatchTarget,
                fieldName: 'ip_reputation_type',
            },
            {
                component: PortMatchComponent as Type<Component>,
                label: this.l10nService.getMessage(l10nKeys.portMatchLabel),
                objectType: NetworkSecurityMatchTarget,
                fieldName: 'vs_port',
            },
            {
                component: GeoMatchComponent as Type<Component>,
                label: this.l10nService.getMessage(l10nKeys.geoMatchesLabel),
                objectType: NetworkSecurityMatchTarget,
                fieldName: 'geo_matches',
            },
        ];
    }
}
